<template>
  <div>
    <Head :title="logistic.code" />
    <h1 class="mb-8 text-3xl font-bold">
      Transaksi
      <span class="text-indigo-400 font-medium"> / </span>
      <Link class="text-indigo-400 hover:text-indigo-600" href="/logistics">Donasi Logistics</Link>
      <span class="text-indigo-400 font-medium"> / </span>
      {{ logistic.code }}
    </h1>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <text-input v-model="form.activity" :error="form.errors.activity" class="pb-8 pr-6 w-full md:w-3/5" label="Kegiatan" />
          <text-input :model-value="operator" class="pb-8 pr-6 w-full md:w-2/5" label="Operator" readonly />
          <select-input v-model="form.organization_id" :error="form.errors.organization_id" class="pb-8 pr-6 w-full md:w-1/2" label="Donatur">
            <option v-for="organization in organizations" :key="organization.id" :value="organization.id">{{ organization.name }}</option>
          </select-input>
          <div class="pb-8 pr-6 flex flex-col w-full lg:w-1/2">
            <label for="date">Tanggal:</label>
            <flat-pickr v-model="form.trans_date" :error="form.errors.trans_date" class="p-2 w-full border rounded mt-2" name="date" />
            <div v-if="form.errors.trans_date" class="form-error">{{ form.errors.trans_date }}</div>
          </div>
          <textarea-input v-model="form.description" :error="form.errors.description" class="pb-8 pr-6 w-full md:w-2/3" label="Keterangan" />
          <div class="w-full" />
          <text-input v-model="form.signature01" :error="form.errors.signature01" class="pb-8 pr-6 w-full md:w-1/3" label="Mengetahui" />
          <text-input v-model="form.signature02" :error="form.errors.signature02" class="pb-8 pr-6 w-full md:w-1/3" label="Penerima" />
          <text-input v-model="form.signature03" :error="form.errors.signature03" class="pb-8 pr-6 w-full md:w-1/3" label="Pengirim" />
        </div>

        <div class="-mr-6 px-8 py-4 border-t border-gray-100">
          <h3 class="text-xl">List berkas pendukung: </h3>

          <ul v-for="(docfile, idx) in logistic.files" :key="idx" class="w-full list-disc">
            <li class="flex gap-2 items-center">
              <span>- </span>
              <a :href="docfile.url" :class="attachmentAccess.download ? 'text-indigo-500 underline' : 'text-red-600'" target="_blank"
                :download="docfile.file_name" :disable="!attachmentAccess.download"> {{
                docfile.file_name }}</a> ( {{ docfile.size }} )
              <a href="" title="Delete attachment file" @click.prevent="destroyAttachment(docfile.uuid)">
                <Icon icon="ic:baseline-delete-forever" class="w-6 h-6 text-red-600" />
              </a>
            </li>
          </ul>

          <file-input v-model="form.attachment" :error="form.errors.attachment" class="pr-6 py-4 w-full md:w-2/3"
            type="file" accept="image/*, application/pdf" label="Browse dan upload file dokumen pendukung (Max 10MB)"
            @update:model-value="uploadAttachment" v-if="attachmentAccess.upload"/>
        </div>

        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button v-if="!logistic.deleted_at" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">Delete Donasi</button>
          <a
            :href="`/logistics/${logistic.id}/print`"
            as="button" target="_blank"
            class="bg-green-500 px-6 py-3 rounded text-white text-sm leading-4 font-bold whitespace-nowrap hover:bg-orange-400 focus:bg-green-400 ml-auto"
          >
            Print PR (PDF)
          </a>
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">Update Donasi</loading-button>
        </div>
      </form>
    </div>
    <h2 class="mt-12 text-2xl font-bold">Details</h2>
    <div class="my-5 flex justify-between">
      <button class="btn-indigo" @click="createMode = true"><span>Create</span> <span class="hidden md:inline">&nbsp;Detail</span></button>
    </div>
    <div class="max-w-4xl bg-white rounded-md shadow overflow-hidden">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-4 pt-6 px-6 w-4/12">Barang</th>
          <th class="pb-4 pt-6 px-6 w-2/12">Lokasi</th>
          <th class="pb-4 pt-6 px-6 text-right w-2/12">Qty</th>
          <th class="pb-4 pt-6 px-6 text-left w-2/12">Satuan</th>
          <th class="pb-4 pt-6 px-6 text-left w-2/12">Keterangan</th>
        </tr>
        <detail
          v-for="logisticDetail in logisticDetails"
          :key="logisticDetail.id"
          :editable-mode="logisticDetail.editable"
          :logistic-detail="logisticDetail"
          :locations="locations"
          :errors="$page.props.errors"
          :units="units"
          @destroy:detail="destroyDetail"
          @edit:detail="editDetail"
          @cancel="cancel('edit')"
          @save="updateDetail"
        />
        <detail
          v-if="createMode"
          :key="0"
          :editable-mode="createMode"
          :logistic-detail="editedItem"
          :locations="locations"
          :errors="$page.props.errors"
          :units="units"
          @cancel="cancel('create')"
          @save="createDetail"
        />
        <tr v-if="logisticDetails.length === 0">
          <td class="px-6 py-4 border-t" colspan="5">No details found.</td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script lang="js">
import { Head, Link } from '@inertiajs/vue3'
import { Icon } from '@iconify/vue'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TextareaInput from '@/Shared/TextareaInput'
import Detail from '@/Pages/Logistics/DetailRow'
import flatPickr from 'vue-flatpickr-component'
import FileInput from '@/Shared/FileInput'
import 'flatpickr/dist/flatpickr.css'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    TextareaInput,
    Detail,
    flatPickr,
    FileInput,
    Icon,
  },
  layout: Layout,
  props: {
    filters: Object,
    logistic: Object,
    organizations: Object,
    operator: String,
    logisticDetails: Array,
    locations: Object,
    units: Object,
    attachmentAccess: Object
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        organization_id: this.logistic.organization_id,
        trans_date: this.logistic.trans_date,
        description: this.logistic.description,
        activity: this.logistic.activity,
        signature01: this.logistic.signature01,
        signature02: this.logistic.signature02,
        signature03: this.logistic.signature03,
        attachment: null,
      }),
      createMode: false,
      editedItem: {
        id: null,
        item: null,
        location: null,
        item_qty: 0,
        deleted_at: null,
        description: null,
        editable: false,
      },
    }
  },
  methods: {
    update() {
      this.form.post(`/logistics/${this.logistic.id}`, {
        preserveScroll: true,
        onSuccess: () => this.form.reset('attachment'),
      })
    },
    destroy() {
      if (confirm('Are you sure you want to delete this logistic?')) {
        this.$inertia.delete(`/logistics/${this.logistic.id}`)
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this logistic?')) {
        this.$inertia.put(`/logistics/${this.logistic.id}/restore`)
      }
    },
    editDetail(detail) {
      let edited = find(this.logisticDetails, ['id', detail.id])
      edited.editable = true
    },
    destroyDetail(detailId) {
      this.$inertia.delete(`/logisticsdetails/${detailId}`)
    },
    uploadAttachment() {
      this.form.attachment && this.update()
    },
    destroyAttachment(uuid) {
      if (confirm('Are you sure you want to delete this attachment file?')) {
        this.$inertia.delete(`/attachment/${uuid}`)
      }
    },
    resetEditedItem() {
      this.editedItem = {
        id: null,
        item: null,
        location: null,
        item_qty: 0,
        deleted_at: null,
        description: null,
        editable: false,
      }
      this.createMode = false
    },
    cancel(action) {
      if(action === 'create')
        this.resetEditedItem()
      if (action === 'edit')
        this.$inertia.reload({ only: ['logisticDetails']})
    },
    createDetail(value) {
      const formDetail = this.$inertia.form({
        logistics_donation_m_id: this.logistic.id,
        item_id: value.item.id,
        item_location_id: value.location.id,
        item_qty: value.item_qty,
        item_unit_id: value.item_unit.id,
        description: value.description,
      })

      formDetail.post('/logisticsdetails', {
        preserveScroll: true,
        onSuccess: () => {
          this.resetEditedItem()
        },
        onError: () => {
          this.editedItem = value
        },
      })
    },
    updateDetail(value) {
      const formDetail = this.$inertia.form({
        item_id: value.item.id,
        item_location_id: value.location.id,
        item_qty: value.item_qty,
        item_unit_id: value.item_unit.id,
        description: value.description,
      })

      formDetail.put(`/logisticsdetails/${value.id}`, {
        preserveScroll: true,
        onError: () => {
          this.editedItem = value
        },
      })
    },
  },
}
</script>

